import React from 'react';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import { useDispatch } from 'react-redux';
import ViewDayIcon from '@material-ui/icons/ViewDay';

import { setMessage } from '../../store/actions/general';
import axios from '../../config/axiosInstance';

// Hook
function usePrevious(value) {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref = React.useRef();
  // Store current value in ref
  React.useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes
  // Return previous value (happens before update in useEffect above)
  return ref.current;
}


function PaypalButtons({
  plan,
  onConfirmPayment,
  planType,
  cantClose,
  values,
  onChangeValue,
  loading,
}) {
  const dispatch = useDispatch();
  const paypalRef = React.useRef();
  const [loaded, setLoaded] = React.useState(false);

  React.useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://www.paypal.com/sdk/js?client-id=AbrOldc4ASFoHW86E_NnEpFhhHbEJiKD-wscqGdPTQEcnzGKbFJZ-y3SlH4WkH-mR95_rnyZg_hcIxpJ&currency=BRL';
    script.addEventListener('load', () => {
      setTimeout(() => {
        setLoaded(true);
      }, 1000);
    });
    document.body.appendChild(script);
  }, []);

  const info = {
    idPlan: plan.uuidOffer,
    priceUser: planType === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice,
    isAnnual: planType !== 'monthly',
    isBillet: false,
    invoice: {
      businessName: values.name,
      cnpj: values.document.replace(/\D+/g, ''),
      address: values.address,
      email: values.email,
      phone: values.phone.replace(/\D+/g, ''),
      houseNumber: values.addressNumber,
      complement: values.complement,
      neighborhood: values.district,
      cityCod: values.cityCod,
      stateCod: values.state,
      postalCode: values.zipCode.replace(/\D+/g, ''),
    },
  };

  const prevLoadedValue = usePrevious(loaded);
  React.useEffect(() => {
    if (prevLoadedValue !== loaded && loaded) {
      window.paypal
        .Buttons({
          createOrder: () => axios.post('/payments/paypal/create-order/v2', info)
            .then(response => response.data.orderID)
            .catch(err => dispatch(setMessage(err))),
          onApprove: async (data) => {
            cantClose(true);
            axios.post('/payments/paypal/capture-transaction', { orderID: data.orderID })
              .then((res) => {
                const response = {
                  type: 'paypal',
                  isAnnual: planType !== 'monthly',
                  title: 'Compra efetuada com sucesso',
                  content: `Agora sua empresa possui o plano ${plan.name} até ${res.expiresIn} (Horário de Fortaleza).`,
                };
                onConfirmPayment(response);
              })
              .catch((err) => {
                cantClose(false);
                dispatch(setMessage(err));
              });
          },
          onError: ((err) => {
            if (typeof err === 'string') {
              dispatch(setMessage(err));
            } else if (typeof err === 'object' && err.message && err.message.name) {
              const msg = `Ops! Evento ${err.message.name} ocorrido. Entre em contato com o suporte para maiores informações`;
              dispatch(setMessage(msg));
            } else {
              dispatch(setMessage('Ocorreu um erro ao processar a solicitação. Entre em contato com o suporte.'));
            }
          }),
        })
        .render(paypalRef.current);
    }
  }, [loaded, info, plan.name, planType, onConfirmPayment, dispatch, cantClose, prevLoadedValue]);

  const prevOption = usePrevious(planType);
  React.useEffect(() => {
    if (prevOption !== planType && loaded) {
      paypalRef.current.removeChild(paypalRef.current.firstChild);
      setLoaded(false);
      setTimeout(() => setLoaded(true), 1000);
    }
  }, [planType, loaded, prevOption]);

  const generateBoleto = () => {
    onChangeValue('loading', true);
    const paymentInfo = {
      idPlan: plan.uuidOffer,
      priceUser: planType === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice,
      isAnnual: planType !== 'monthly',
      isBillet: true,
      invoice: {
        businessName: values.name,
        cnpj: values.document.replace(/\D+/g, ''),
        address: values.address,
        email: values.email,
        phone: values.phone.replace(/\D+/g, ''),
        houseNumber: values.addressNumber,
        complement: values.complement,
        neighborhood: values.district,
        cityCod: values.cityCod,
        stateCod: values.state,
        postalCode: values.zipCode.replace(/\D+/g, ''),
      },
    };

    axios.post('/payments/paypal/create-order/v2', paymentInfo)
      .then(({ data }) => {
        const response = {
          type: 'boleto',
          isAnnual: planType !== 'monthly',
          boletoPath: data.url,
          invoiceId: data.invoiceId,
          paymentId: data.paymentId,
          title: 'Aguardando Pagamento',
          content: `O boleto será enviado para o email informado (${values.email}). Confira sua caixa de entrada!`,
        };

        onConfirmPayment(response);
      })
      .catch((err) => {
        onChangeValue('loading', false);
        dispatch(setMessage(err));
      });
  };

  if (loaded) {
    return (
      <div>
        {loading && (
          <Typography
            align="center"
            component="div"
            style={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
              padding: '1em 0',
            }}
          >
            Processando Pagamento
            <CircularProgress size={30} color="primary" />
          </Typography>
        )}
        <div style={{ padding: '16px 0px', opacity: loading ? 0 : 1, position: loading && 'absolute' }}>
          <Button
            variant="contained"
            style={{
              width: '100%',
              height: '55px',
            }}
            onClick={() => generateBoleto()}
          >
            <ViewDayIcon style={{ marginRight: '0.25em' }}/>
             BOLETO
          </Button>
        </div>
        <div ref={paypalRef} style={{ opacity: loading ? 0 : 1, position: loading && 'absolute' }} />
      </div>
    );
  }

  return (
    <Typography align="center" component="div">
      <CircularProgress size={30} color="primary" />
    </Typography>
  );
}

export default PaypalButtons;
